import { motion } from 'framer-motion';
import { X, Check, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

interface ComparisonItem {
  problem: string;
  solution: string;
  improvement?: string;
}

interface BeforeAfterSliderProps {
  items: ComparisonItem[];
}

const BeforeAfterSlider = ({ items }: BeforeAfterSliderProps) => {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
        {/* Before - Traditional */}
        <Card className="p-8 h-full bg-gradient-to-br from-red-500/5 to-red-600/5 border-red-500/20 relative overflow-hidden">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center border border-red-500/30">
              <X className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t('industries.before_after_before')}</h3>
              <p className="text-xs text-red-400 font-semibold">{t('industries.before_after_traditional')}</p>
            </div>
          </div>
          
          <ul className="space-y-4">
            {items.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-red-500/20"
              >
                <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-sm text-muted-foreground leading-relaxed">{item.problem}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {/* After - With Holionix */}
        <Card className="p-8 h-full bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-green-500/20 relative overflow-hidden">
          <div className="absolute top-4 right-4 gradient-brand text-white text-[10px] font-bold px-3 py-1.5 rounded-full shadow-lg">
            {t('industries.before_after_with_holionix')}
          </div>
          
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl gradient-brand flex items-center justify-center shadow-lg">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{t('industries.before_after_after')}</h3>
              <p className="text-xs text-green-400 font-semibold">{t('industries.before_after_ai_powered')}</p>
            </div>
          </div>
          
          <ul className="space-y-4">
            {items.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-3 p-3 rounded-lg bg-background/50 border border-green-500/20"
              >
                <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="flex-grow">
                  <span className="text-sm font-medium leading-relaxed block mb-1.5">{item.solution}</span>
                  {item.improvement && (
                    <div className="flex items-center gap-1.5 text-xs text-green-400 font-bold">
                      <TrendingUp className="w-3.5 h-3.5" />
                      {item.improvement}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Center Arrow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:block">
        <div className="w-16 h-16 rounded-full gradient-brand flex items-center justify-center shadow-2xl border-4 border-background">
          <span className="text-2xl">→</span>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
